/**
 * PURPOSE: Administrar un circuito acumulado de Rugby sin editar CSV ni IDs manualmente.
 * RESPONSIBILITIES: Crear/editar metadata, ordenar fechas, seleccionar instituciones y recalcular resultados.
 * COLLABORATORS: cumulativeSeriesService.js, ConfigCategoryArea.jsx y cumulativeSeries.service.js.
 * ROUTE: winscore/src/components/Cards/CardConfiguration/CardCumulativeSeries/CardCumulativeSeries.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  FiArrowDown,
  FiArrowUp,
  FiCalendar,
  FiPlus,
  FiRefreshCw,
  FiSave,
  FiTrash2,
} from "react-icons/fi";

import {
  getCumulativeSeriesEditor,
  recalculateCumulativeEvent,
  recalculateCumulativeSeries,
  saveCumulativeSeries,
} from "../../../../services/cumulativeSeriesService.js";
import { getChampionshipsByCompany } from "../../../../services/championship.js";
import { useEvent } from "../../../../hooks/api/useEvent.js";

const DEFAULT_DESCRIPTION =
  "Clasificación general con el acumulado de puntos por club y estadísticas del circuito.";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.msg || error?.message || fallback;

const CardCumulativeSeries = () => {
  const { idevent, idsport } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState("");
  const [candidateEvents, setCandidateEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [companyEventIds, setCompanyEventIds] = useState(null);
  const [form, setForm] = useState({
    id: null,
    title: "",
    description: DEFAULT_DESCRIPTION,
    state: 1,
    events: [],
  });

  const loadEditor = useCallback(async () => {
    if (!idevent || !idsport) return;
    setLoading(true);
    try {
      const payload = await getCumulativeSeriesEditor({ idevent, idsport });
      const candidates = Array.isArray(payload?.candidateEvents)
        ? payload.candidateEvents
        : [];
      setCandidateEvents(candidates);

      if (payload?.series) {
        setForm({
          id: payload.series.id,
          title: payload.series.title || "",
          description: payload.series.description || DEFAULT_DESCRIPTION,
          state: Number(payload.series.state) === 0 ? 0 : 1,
          events: Array.isArray(payload.series.events)
            ? payload.series.events.map((event) => ({
                idevent: Number(event.idevent || event.id),
                institutionIds: Array.isArray(event.institutionIds)
                  ? event.institutionIds.map(Number)
                  : [],
              }))
            : [],
        });
      } else {
        const current = candidates.find(
          (event) => Number(event.id) === Number(idevent)
        );
        setForm({
          id: null,
          title: "",
          description: DEFAULT_DESCRIPTION,
          state: 1,
          events: current
            ? [
                {
                  idevent: Number(current.id),
                  institutionIds: current.institutions.map((item) => Number(item.id)),
                },
              ]
            : [],
        });
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "No se pudo cargar el circuito acumulado."));
    } finally {
      setLoading(false);
    }
  }, [idevent, idsport]);

  useEffect(() => {
    loadEditor();
  }, [loadEditor]);

  const { data: currentEventData } = useEvent(idevent);
  const idcompany = currentEventData?.idcompany
    ? Number(currentEventData.idcompany)
    : null;

  useEffect(() => {
    if (!idcompany) {
      setCompanyEventIds(null);
      return undefined;
    }
    let cancelled = false;
    getChampionshipsByCompany({ idcompany })
      .then((championships) => {
        if (cancelled) return;
        setCompanyEventIds(
          new Set(championships.map((item) => Number(item.idchampionship)))
        );
      })
      .catch(() => {
        if (!cancelled) setCompanyEventIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [idcompany]);

  const candidateMap = useMemo(
    () => new Map(candidateEvents.map((event) => [Number(event.id), event])),
    [candidateEvents]
  );
  const selectedIds = useMemo(
    () => new Set(form.events.map((event) => Number(event.idevent))),
    [form.events]
  );
  const availableEvents = candidateEvents.filter(
    (event) =>
      !event.locked &&
      !selectedIds.has(Number(event.id)) &&
      Number(event.id) !== Number(idevent) &&
      Boolean(companyEventIds?.has(Number(event.id)))
  );

  const addEvent = () => {
    const eventId = Number(selectedEventId);
    const event = candidateMap.get(eventId);
    if (!event || selectedIds.has(eventId)) return;
    setForm((current) => ({
      ...current,
      events: [
        ...current.events,
        {
          idevent: eventId,
          institutionIds: event.institutions.map((item) => Number(item.id)),
        },
      ],
    }));
    setSelectedEventId("");
  };

  const moveEvent = (index, direction) => {
    setForm((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.events.length) return current;
      const events = [...current.events];
      [events[index], events[target]] = [events[target], events[index]];
      return { ...current, events };
    });
  };

  const removeEvent = (ideventToRemove) => {
    setForm((current) => ({
      ...current,
      events: current.events.filter(
        (event) => Number(event.idevent) !== Number(ideventToRemove)
      ),
    }));
  };

  const toggleInstitution = (eventId, institutionId) => {
    setForm((current) => ({
      ...current,
      events: current.events.map((event) => {
        if (Number(event.idevent) !== Number(eventId)) return event;
        const selected = new Set(event.institutionIds.map(Number));
        if (selected.has(Number(institutionId))) selected.delete(Number(institutionId));
        else selected.add(Number(institutionId));
        return { ...event, institutionIds: Array.from(selected) };
      }),
    }));
  };

  const toggleAllInstitutions = (eventId) => {
    const candidate = candidateMap.get(Number(eventId));
    if (!candidate) return;
    setForm((current) => ({
      ...current,
      events: current.events.map((event) => {
        if (Number(event.idevent) !== Number(eventId)) return event;
        const allIds = candidate.institutions.map((item) => Number(item.id));
        const allSelected =
          allIds.length > 0 && allIds.every((id) => event.institutionIds.includes(id));
        return { ...event, institutionIds: allSelected ? [] : allIds };
      }),
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Escribe el nombre público del circuito.");
      return;
    }
    if (!form.events.length) {
      toast.error("Agrega al menos una fecha al circuito.");
      return;
    }
    if (form.events.some((event) => !event.institutionIds.length)) {
      toast.error("Cada fecha debe tener al menos una institución seleccionada.");
      return;
    }

    setSaving(true);
    try {
      await saveCumulativeSeries({
        seriesId: form.id,
        payload: {
          idsport: Number(idsport),
          title: form.title.trim(),
          description: form.description.trim(),
          state: form.state,
          events: form.events,
        },
      });
      toast.success(form.id ? "¡Circuito actualizado!" : "¡Circuito creado!");
      await loadEditor();
    } catch (error) {
      toast.error(getErrorMessage(error, "No se pudo guardar el circuito."));
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculateCurrent = async () => {
    if (!form.id) return;
    setRecalculating("event");
    try {
      const result = await recalculateCumulativeEvent({
        idevent,
        idsport,
        seriesId: form.id,
      });
      toast.success(`Fecha actualizada: ${result.rowsUpdated || 0} filas.`);
    } catch (error) {
      toast.error(getErrorMessage(error, "No se pudo recalcular la fecha."));
    } finally {
      setRecalculating("");
    }
  };

  const handleRecalculateAll = async () => {
    if (!form.id) return;
    setRecalculating("series");
    try {
      const result = await recalculateCumulativeSeries(form.id);
      toast.success(`Circuito actualizado: ${result.rowsUpdated || 0} filas.`);
    } catch (error) {
      toast.error(getErrorMessage(error, "No se pudo recalcular el circuito."));
    } finally {
      setRecalculating("");
    }
  };

  return (
    <div className="flex justify-center bg-gray-100 p-4">
      <div className="w-full max-w-5xl rounded-2xl border border-emerald-100 bg-white p-5 shadow-lg sm:p-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white">
              <FiCalendar />
            </span>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
                Rugby
              </p>
              <h2 className="text-2xl font-black text-slate-900">Circuito acumulado</h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                Relaciona fechas e instituciones una sola vez. Hayllis publicará el botón,
                título y estadísticas automáticamente.
              </p>
            </div>
          </div>
          {form.id ? (
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
              Circuito #{form.id}
            </span>
          ) : null}
        </div>

        {loading ? (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-5 text-center font-bold text-emerald-700">
            Cargando configuración acumulada...
          </div>
        ) : (
          <div className="grid gap-5">
            <div className="grid gap-4 md:grid-cols-[1.1fr,1.4fr]">
              <label className="grid gap-2 text-sm font-bold text-slate-700">
                Nombre público del circuito
                <input
                  value={form.title}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, title: event.target.value }))
                  }
                  placeholder="Circuito Nacional de 7'S femenino"
                  className="rounded-xl border border-slate-300 px-4 py-2.5 font-normal outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </label>
              <label className="grid gap-2 text-sm font-bold text-slate-700">
                Descripción pública
                <input
                  value={form.description}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  className="rounded-xl border border-slate-300 px-4 py-2.5 font-normal outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </label>
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-end">
              <label className="grid flex-1 gap-2 text-sm font-bold text-slate-700">
                Agregar otra fecha
                <select
                  value={selectedEventId}
                  onChange={(event) => setSelectedEventId(event.target.value)}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 font-normal outline-none focus:border-emerald-500"
                >
                  <option value="">Seleccionar evento...</option>
                  {availableEvents.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.name} (#{event.id})
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={addEvent}
                disabled={!selectedEventId}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <FiPlus /> Agregar fecha
              </button>
            </div>

            <div className="grid gap-3">
              {form.events.map((event, index) => {
                const candidate = candidateMap.get(Number(event.idevent));
                const institutions = candidate?.institutions || [];
                const allSelected =
                  institutions.length > 0 &&
                  institutions.every((item) =>
                    event.institutionIds.includes(Number(item.id))
                  );
                return (
                  <section
                    key={event.idevent}
                    className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                  >
                    <div className="flex flex-col gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600">
                          Fecha {index + 1}
                        </p>
                        <h3 className="font-black text-slate-900">
                          {candidate?.name || `Evento ${event.idevent}`}
                          <span className="ml-2 text-xs font-bold text-slate-400">
                            #{event.idevent}
                          </span>
                        </h3>
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => moveEvent(index, -1)} disabled={index === 0} className="rounded-lg p-2 text-slate-600 hover:bg-white disabled:opacity-30" title="Subir fecha"><FiArrowUp /></button>
                        <button type="button" onClick={() => moveEvent(index, 1)} disabled={index === form.events.length - 1} className="rounded-lg p-2 text-slate-600 hover:bg-white disabled:opacity-30" title="Bajar fecha"><FiArrowDown /></button>
                        <button type="button" onClick={() => removeEvent(event.idevent)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" title="Quitar fecha"><FiTrash2 /></button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <p className="text-xs font-bold text-slate-500">
                          {event.institutionIds.length} de {institutions.length} instituciones
                        </p>
                        <button type="button" onClick={() => toggleAllInstitutions(event.idevent)} className="text-xs font-black text-emerald-700 hover:text-emerald-800">
                          {allSelected ? "Quitar todas" : "Seleccionar todas"}
                        </button>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {institutions.map((institution) => {
                          const checked = event.institutionIds.includes(Number(institution.id));
                          return (
                            <label key={institution.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition ${checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleInstitution(event.idevent, institution.id)} className="h-4 w-4 accent-emerald-600" />
                              {institution.logo ? <img src={institution.logo} alt="" className="h-8 w-8 rounded-lg object-contain" /> : <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-xs font-black text-slate-500">{institution.abrev?.slice(0, 2) || "--"}</span>}
                              <span className="min-w-0 truncate text-sm font-bold text-slate-700">{institution.abrev || institution.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-100 pt-5 lg:flex-row lg:items-center lg:justify-between">
              <label className="flex items-center gap-3 text-sm font-bold text-slate-700">
                <input
                  type="checkbox"
                  checked={form.state === 1}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      state: event.target.checked ? 1 : 0,
                    }))
                  }
                  className="toggle-checkbox"
                />
                Circuito activo y visible
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                {form.id ? (
                  <>
                    <button type="button" onClick={handleRecalculateCurrent} disabled={Boolean(recalculating) || !selectedIds.has(Number(idevent))} className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-bold text-blue-700 disabled:cursor-not-allowed disabled:opacity-40">
                      <FiRefreshCw className={recalculating === "event" ? "animate-spin" : ""} /> Recalcular fecha actual
                    </button>
                    <button type="button" onClick={handleRecalculateAll} disabled={Boolean(recalculating)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-bold text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40">
                      <FiRefreshCw className={recalculating === "series" ? "animate-spin" : ""} /> Recalcular circuito
                    </button>
                  </>
                ) : null}
                <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-black text-white shadow-lg shadow-emerald-500/20 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">
                  <FiSave /> {saving ? "Guardando..." : "Guardar circuito"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardCumulativeSeries;
